import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import PanelListWithImages from '../components/PanelListWithImages';
import PanelListLinks from '../components/PanelListLinks';
import PanelListText from '../components/PanelListText';
import JourneyOverview from '../components/JourneyOverview';
import StepCard from '../components/StepCard';
import { usePageTracking } from '../hooks/usePageTracking';
import { AdminService, ProgramConfig } from '../services/adminService';
import { progressService, ProgressItem } from '../services/progressService';
import styles from '../styles/saaHomePage.module.css';

interface SAAHomePageProps {
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
}

const SAAHomePage: React.FC<SAAHomePageProps> = ({ user, onLogout, onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState<ProgramConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  
  // Determine active tab from URL or default
  const getInitialTab = (): 'tab1' | 'tab2' | 'tab3' => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'meetings') return 'tab3';
    if (tabParam === 'resources') return 'tab2';
    if (tabParam === 'steps') return 'tab1';
    // Default: Work My Steps for logged in, Resources for public
    return user ? 'tab1' : 'tab2';
  };
  
  const [activeTab, setActiveTab] = useState<'tab1' | 'tab2' | 'tab3'>(getInitialTab());
  
  // Step progress state (for Tab 1)
  const [stepProgress, setStepProgress] = useState<Record<number, ProgressItem | null>>({});
  const [isInitializing, setIsInitializing] = useState(true);
  const stepProgressRef = useRef<Record<number, ProgressItem | null>>({});

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: true }
  ];

  // Track page visit
  usePageTracking('SAA Homepage');

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, []);

  // Load step progress when user is available and tab1 is active
  useEffect(() => {
    if (user && activeTab === 'tab1') {
      loadStepProgress();
    }
  }, [user, activeTab]);

  // Keep ref in sync with state
  useEffect(() => {
    stepProgressRef.current = stepProgress;
  }, [stepProgress]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const programConfig = await AdminService.getProgramConfig('saa');
      setConfig(programConfig);
      setError(null);
    } catch (err) {
      console.error('Error loading SAA config:', err);
      setError('Failed to load page configuration. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const loadStepProgress = async () => {
    if (!user) return;
    
    setIsInitializing(true);
    try {
      await progressService.initializeProgress();
      const progress = await progressService.getAllStepProgress('SAA', '12-steps');
      setStepProgress(progress);
    } catch (err) {
      console.error('Error loading step progress:', err);
    } finally {
      setIsInitializing(false);
    }
  };

  const getStepStatus = (stepNumber: number): 'not-started' | 'in-progress' | 'completed' => {
    if (!user) return 'not-started';
    
    const progress = stepProgress[stepNumber];
    if (!progress) return 'not-started';
    
    if (progress.completed) return 'completed';
    
    if (progress.data && progress.data.actions && Array.isArray(progress.data.actions)) {
      const hasCompletedAction = progress.data.actions.some((action: any) => action.completed);
      if (hasCompletedAction) return 'in-progress';
    }
    
    return 'not-started';
  };

  const handleStepClick = (stepNumber: number) => {
    navigate(`/saa/12-steps/step-${stepNumber}`);
  };

  const renderPanel = (panel: any) => {
    switch (panel.format) {
      case 'list_with_images':
        return (
          <PanelListWithImages
            key={panel.id}
            title={panel.title}
            subtext={panel.subtext}
            items={panel.items}
          />
        );
      case 'list_links':
        return (
          <PanelListLinks
            key={panel.id}
            title={panel.title}
            subtext={panel.subtext}
            items={panel.items}
          />
        );
      case 'list_text':
        return (
          <PanelListText
            key={panel.id}
            title={panel.title}
            subtext={panel.subtext}
            items={panel.items}
          />
        );
      default:
        return null;
    }
  };

  // Step data with descriptions
  const steps = [
    {
      number: 1,
      title: 'We admitted we were powerless over our addictive sexual behavior - that our lives had become unmanageable.',
      description: 'The first step involves honest self-assessment and admitting powerlessness over sexual addiction.',
      path: '/saa/12-steps/step-1'
    },
    {
      number: 2,
      title: 'Came to believe that a Power greater than ourselves could restore us to sanity.',
      description: 'Developing faith in a higher power that can help restore mental and emotional health.',
      path: '/saa/12-steps/step-2'
    },
    {
      number: 3,
      title: 'Made a decision to turn our will and our lives over to the care of God as we understood God.',
      description: 'Surrendering control and trusting in a higher power for guidance and strength.',
      path: '/saa/12-steps/step-3'
    },
    {
      number: 4,
      title: 'Made a searching and fearless moral inventory of ourselves.',
      description: 'A thorough self-examination of resentments, fears, and harms done to others.',
      path: '/saa/12-steps/step-4'
    },
    {
      number: 5,
      title: 'Admitted to God, to ourselves, and to another human being the exact nature of our wrongs.',
      description: 'Sharing the inventory with a trusted person and experiencing the relief of honesty.',
      path: '/saa/12-steps/step-5'
    },
    {
      number: 6,
      title: 'Were entirely ready to have God remove all these defects of character.',
      description: 'Becoming willing to let go of character defects and negative patterns.',
      path: '/saa/12-steps/step-6'
    },
    {
      number: 7,
      title: 'Humbly asked God to remove our shortcomings.',
      description: 'Asking for help in removing character defects and developing humility.',
      path: '/saa/12-steps/step-7'
    },
    {
      number: 8,
      title: 'Made a list of all persons we had harmed and became willing to make amends to them all.',
      description: 'Identifying those harmed by our actions and preparing to make amends.',
      path: '/saa/12-steps/step-8'
    },
    {
      number: 9,
      title: 'Made direct amends to such people wherever possible, except when to do so would injure them or others.',
      description: 'Taking action to repair relationships and make amends where appropriate.',
      path: '/saa/12-steps/step-9'
    },
    {
      number: 10,
      title: 'Continued to take personal inventory and when we were wrong promptly admitted it.',
      description: 'Ongoing self-examination and immediate admission of mistakes.',
      path: '/saa/12-steps/step-10'
    },
    {
      number: 11,
      title: 'Sought through prayer and meditation to improve our conscious contact with God as we understood God, praying only for knowledge of God\'s will for us and the power to carry that out.',
      description: 'Developing spiritual practices and seeking guidance through prayer and meditation.',
      path: '/saa/12-steps/step-11'
    },
    {
      number: 12,
      title: 'Having had a spiritual awakening as the result of these steps, we tried to carry this message to sex addicts and to practice these principles in all our affairs.',
      description: 'Sharing the message of recovery and living by spiritual principles in all areas of life.',
      path: '/saa/12-steps/step-12'
    }
  ];

  if (loading) {
    return (
      <div className={styles.saaHomePage}>
        <Header 
          breadcrumbs={breadcrumbs}
          user={user}
          onLogout={onLogout}
          onLogin={onLogin}
        />
        <main className={styles.mainContent}>
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Loading...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className={styles.saaHomePage}>
        <Header 
          breadcrumbs={breadcrumbs}
          user={user}
          onLogout={onLogout}
          onLogin={onLogin}
        />
        <main className={styles.mainContent}>
          <div className={styles.errorState}>
            <p>{error || 'Failed to load page configuration'}</p>
            <button onClick={loadConfig} className={styles.retryButton}>
              Retry
            </button>
          </div>
        </main>
      </div>
    );
  }

  const saaIconUrl = 'https://recoverycommons.com.s3.amazonaws.com/programs/saa/icons/from-shame-to-grace.png';

  return (
    <div className={styles.saaHomePage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      <main className={styles.mainContent}>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <div className={styles.titleRow}>
            <h1 className={styles.saaTitle}>{config.title}</h1>
            <img 
              src={saaIconUrl} 
              alt="SAA" 
              className={styles.saaIcon}
            />
          </div>
          <div className={styles.aboutContent}>
            {!aboutExpanded ? (
              <>
                <div className={styles.aboutPreview}>
                  {config.subtitle}{' '}
                  <button className={styles.readMoreBtn} onClick={() => setAboutExpanded(true)}>
                    read more
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={styles.aboutFull}>
                  <p>{config.subtitle}</p>
                  <p>This page provides everything you need for your SAA recovery journey. Use the tabs below to:</p>
                  <ul className={styles.aboutList}>
                    <li><strong>Work My Steps</strong> - Track your progress through the 12 steps of recovery. Each step includes guided actions, recovery literature, and tools to help you work through the program systematically.</li>
                    <li><strong>Meetings & Workshops</strong> - Find local and online SAA meetings, learn about workshop opportunities, and connect with others in recovery.</li>
                    <li><strong>Resources & Reading</strong> - Explore personal recovery articles, recommended books, and inspirational quotes to support your journey.</li>
                  </ul>
                  <p>SAA is not allied with any sect, denomination, politics, organization, or institution. We do not wish to engage in any controversy, and we neither endorse nor oppose any causes. Our primary purpose is to stay sexually sober and help other sex addicts achieve sobriety.</p>
                  <p>The only requirement for membership is a desire to stop addictive sexual behavior. There are no dues or fees for SAA membership. We are self-supporting through our own contributions.</p>
                </div>
                <button className={styles.readMoreBtn} onClick={() => setAboutExpanded(false)}>
                  show less
                </button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className={styles.tabsContainer}>
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${activeTab === 'tab1' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('tab1')}
            >
              Work My Steps
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'tab2' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('tab2')}
            >
              {config.tab2?.name || 'Resources & Reading'}
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'tab3' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('tab3')}
            >
              Meetings & Workshops
            </button>
          </div>

          {/* Tab 1 Content: Work My Steps */}
          {activeTab === 'tab1' && (
            <div className={styles.tabContent}>
              {isInitializing && user && (
                <div className={styles.loadingMessage}>
                  <div>⏳ Loading your progress...</div>
                  <div className={styles.loadingSubtext}>Please wait while we retrieve your step data</div>
                </div>
              )}

              {!user && (
                <div className={styles.loginPrompt}>
                  <div className={styles.loginPromptContent}>
                    <h2>Track Your Progress</h2>
                    <p>
                      Login or create an account to track your progress through the 12 steps and receive 
                      personalized guidance and support in your recovery journey.
                    </p>
                    <div className={styles.buttonGroup}>
                      <button onClick={onLogin} className={styles.loginButton}>
                        Login
                      </button>
                      <button onClick={onLogin} className={styles.createAccountButton}>
                        Create Account
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!isInitializing && user && (
                <>
                  <JourneyOverview user={user} stepProgress={stepProgress} />
                  <div className={styles.stepsGrid}>
                    {steps.map((step) => (
                      <StepCard
                        key={step.number}
                        stepNumber={step.number}
                        title={step.title}
                        description={step.description}
                        status={getStepStatus(step.number)}
                        isLoggedIn={!!user}
                        stepPath={step.path}
                        onStepClick={() => handleStepClick(step.number)}
                        onLoginClick={onLogin}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Tab 2 Content: Resources & Reading Panels */}
          {activeTab === 'tab2' && (
            <div className={styles.tabContent}>
              {config.tab2.header_text && (
                <p className={styles.tab2Header}>{config.tab2.header_text}</p>
              )}
              <div className={styles.panelsContainer}>
                {config.tab2?.panels
                  ?.sort((a: any, b: any) => a.order - b.order)
                  .map((panel: any) => renderPanel(panel))}
              </div>
            </div>
          )}

          {/* Tab 3 Content: Meetings & Workshops */}
          {activeTab === 'tab3' && (
            <div className={styles.tabContent}>
              <div className={styles.placeholderContent}>
                <h2>Meetings & Workshops</h2>
                <p>Content coming soon...</p>
                <p>This section will include:</p>
                <ul>
                  <li>Local meeting finder</li>
                  <li>Online meeting information</li>
                  <li>Workshop schedules</li>
                  <li>Meeting formats and guidelines</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SAAHomePage;
